import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
// read excel file
import readXlsxFile from "read-excel-file";

import { showNotification, RestMethods, FETCH_START, FETCH_END, SAVING_START, SAVING_END } from "jazasoft";

// helper
import useQuery from "../../hooks/useQuery";
import { authServerUrl, appId, dataProvider } from "../../App";
import { isEmpty, getTrimmedList, listFromCsv, getDistinctValues } from "../../utils/helpers";
import handleError from "../../utils/handleError";
import { REGEX_USERNAME, REGEX_EMAIL, REGEX_MOBILE } from "../../utils/regex";
import { Role } from "../../utils/types";
import useUnit from "../../hooks/useUnit";

const useCreateEdit = props => {
  const { history, id } = props;
  // api call
  const { data: apiList = {} } = useQuery({ url: "apis", params: { appId: "iam" } });
  const { data: appUser = {} } = useQuery(id && { url: `users/${id}` });
  const apiKey = apiList[0]?.apiKey;

  // store
  const authState = useSelector(state => state.jazasoft.auth) || {};
  const app = authState?.userAppList?.find(e => e.app?.appId === appId)?.app || {};

  const { unitChoices, allUnitList, userUnitIds } = useUnit(appUser);

  // state
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [iamUser, setIamUser] = useState([]);
  const [errorList, setErrorList] = useState(null);

  const dispatch = useDispatch();

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const fetchRoles = async () => {
      const options = {
        url: `${authServerUrl}/roles`,
        method: "get",
        headers: {
          "x-api-key": apiKey
        }
      };
      const response = await axios.request(options);
      if (response.status === 200) {
        const roles = response.data?.content || [];
        setRoles(roles);
      }
    };
    const fetchPermissions = async () => {
      const options = {
        url: `${authServerUrl}/permissions`,
        method: "get",
        params: { search: `key=in=('customerId', 'warehouseId')`, size: 1000 },
        headers: {
          "x-api-key": apiKey
        }
      };
      const response = await axios.request(options);
      if (response.status === 200) {
        const permissions = response.data?.content || [];
        setPermissions(permissions);
      }
    };

    if (!isEmpty(authServerUrl) && !isEmpty(apiKey)) {
      fetchRoles();
      fetchPermissions();
    }
  }, [authServerUrl, apiKey]);

  useEffect(() => {
    const fetchUser = async () => {
      const options = {
        url: `${authServerUrl}/users/${id}`,
        method: "get",
        headers: { "x-api-key": apiKey }
      };
      try {
        const response = await axios.request(options);
        if (response.status === 200) {
          const iamUser = response.data || {};
          setIamUser(iamUser);
        }
      } catch (error) {
        handleError(error, dispatch);
      }
    };

    if (id && !isEmpty(apiKey) && !isEmpty(authServerUrl)) {
      fetchUser();
    }
  }, [id, apiKey, authServerUrl]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const choicesCustomer = !isEmpty(permissions) ? permissions.filter(e => e.key === "customerId") : [];
  const choicesWarehouse = !isEmpty(permissions) ? permissions.filter(e => e.key === "warehouseId") : [];

  const onSave = async values => {
    const { unitIds = [], roleIds, customerIds, warehouseIds, ...restUser } = values;

    // Save data in IAM first, then in app
    let options = {
      url: `${authServerUrl}/users`,
      method: "post",
      data: { ...restUser, apps: [{ id: app?.id, unitIds: unitIds?.join(",") || authState?.units, roleIds, customerIds, warehouseIds }] },
      headers: { "x-api-key": apiKey }
    };
    dispatch({ type: FETCH_START });
    dispatch({ type: SAVING_START });

    try {
      let response = await axios.request(options);
      if (response.status === 201 || response.status === 200) {
        let iamUser = response.data;
        // Then Save data in Application
        options = {
          url: `users`,
          method: "post",
          data: {
            ...values,
            ...iamUser,
            roles: iamUser.roleList && iamUser.roleList.map(r => r.role && r.role.roleId).join(","),
            unitIds: unitIds?.join(",") || authState?.units,
            customerIds: iamUser.permissionList
              .filter(p => p.key === "customerId")
              ?.map(e => e.value)
              ?.join(","),
            warehouseIds: iamUser.permissionList
              .filter(p => p.key === "warehouseId")
              ?.map(e => e.value)
              ?.join(",")
          }
        };
        response = await dataProvider(RestMethods.CUSTOM, undefined, options);
        if (response.status === 200 || response.status === 201) {
          dispatch(showNotification("js.notification.created", { messageArgs: { element: "User" } }));
          history.push("/users");
        }
      }
      dispatch({ type: FETCH_END });
      dispatch({ type: SAVING_END });
    } catch (err) {
      handleError(err?.response?.data || err, dispatch);
      dispatch({ type: FETCH_END });
      dispatch({ type: SAVING_END });
    }
  };

  const onUpdate = async ({ authorities, unitIds = [], roleIds, customerIds, warehouseIds, action = "default", ...user }) => {
    const { authorities: authoritiesData, ...rest } = iamUser || {};
    const userData = action === "default" ? user : rest;

    const otherUnitIds = listFromCsv(appUser.unitIds).filter(unitId => !userUnitIds?.includes(unitId));
    const finalUnitIds = action === "default" ? getDistinctValues([...otherUnitIds, ...unitIds]) : listFromCsv(appUser?.unitIds);

    // Save data in IAM first, then in app
    let options = {
      url: `${authServerUrl}/users/${id}`,
      method: "put",
      params: { action },
      data: {
        ...userData,
        apps: [{ id: app?.id, roleIds, customerIds, warehouseIds, unitIds: finalUnitIds.join(",") || authState?.units }]
      },
      headers: {
        "x-api-key": apiKey
      }
    };
    dispatch({ type: FETCH_START });
    dispatch({ type: SAVING_START });

    try {
      let response = await axios.request(options);
      if (response.status === 201 || response.status === 200) {
        let iamUser = response.data;
        let name = `${iamUser.firstName} ${iamUser.lastName}`;
        options = {
          url: `users/${id}`,
          method: "put",
          data: {
            ...user,
            ...iamUser,
            name: name.trim(),
            roles: iamUser.roleList && iamUser.roleList.map(r => r.role && r.role.roleId).join(","),
            unitIds: finalUnitIds.join(",") || authState?.units,
            customerIds: iamUser.permissionList
              .filter(p => p.key === "customerId")
              ?.map(e => e.value)
              ?.join(","),
            warehouseIds: iamUser.permissionList
              .filter(p => p.key === "warehouseId")
              ?.map(e => e.value)
              ?.join(",")
          }
        };
        response = await dataProvider(RestMethods.CUSTOM, undefined, options);
        if (response.status === 200 || response.status === 201) {
          dispatch(showNotification("js.notification.updated", { messageArgs: { element: "User", smart_count: 1 } }));
          history.push("/users");
        }
        dispatch({ type: FETCH_END });
        dispatch({ type: SAVING_END });
      }
    } catch (err) {
      handleError(err?.response?.data || err, dispatch);
      dispatch({ type: FETCH_END });
      dispatch({ type: SAVING_END });
    }
  };

  const onDelete = async userId => {
    // First delete user in Application
    let options = {
      url: `users/${userId}`,
      method: "delete"
    };
    dispatch({ type: FETCH_START });
    dispatch({ type: SAVING_START });
    try {
      let response = await dataProvider(RestMethods.CUSTOM, undefined, options);
      // Then delete in Auth Server
      if (response.status === 200 || response.status === 204) {
        options = {
          url: `${authServerUrl}/users/${userId}`,
          method: "delete",
          headers: {
            "x-api-key": apiKey
          }
        };
        response = await axios.request(options);
        if (response.status === 204) {
          dispatch(showNotification(`js.notification.deleted`, { messageArgs: { smart_count: 1, element: "User" } }));
          history.push("/users");
        }
      }
      dispatch({ type: FETCH_END });
      dispatch({ type: SAVING_END });
    } catch (err) {
      handleError(err?.response?.data || err, dispatch);
      dispatch({ type: FETCH_END });
      dispatch({ type: SAVING_END });
    }
  };

  // Excel upload validation and submission

  const getSchema = () => {
    return {
      USERNAME: {
        prop: "username",
        type: String,
        validate: validate({ column: "username" })
      },
      "FULL NAME": {
        prop: "fullName",
        type: String
      },
      EMAIL: {
        prop: "email",
        type: String,
        validate: validate({ column: "email" })
      },
      MOBILE: {
        prop: "mobile",
        type: String,
        validate: validate({ column: "mobile" })
      },
      ROLES: {
        prop: "roles",
        type: String,
        validate: validate({ column: "roles" })
      },
      CUSTOMERS: {
        prop: "customers",
        type: String,
        validate: validate({ column: "customers" })
      }
    };
  };

  const validate =
    ({ column }) =>
    value => {
      value = value && String(value);
      if (column === "username") {
        if (!REGEX_USERNAME.test(value.trim())) {
          throw new Error(
            `Invalid value for Username. Username should consists of Alphabets, numbers and (hyphen, underscore, dot) special characters only and starting with Alphabet`
          );
        }
      } else if (column === "email") {
        if (!REGEX_EMAIL.test(value.trim())) {
          throw new Error(`Invalid value for Email.`);
        }
      } else if (column === "mobile") {
        if (!REGEX_MOBILE.test(value.trim())) {
          throw new Error(`Invalid value for Mobile. Mobile number must be 10 digit numeric value`);
        }
      } else if (column === "roles" && value) {
        const valueRoles = getTrimmedList(value).map(e => e.toLowerCase());
        const rolesAuth = roles?.map(e => e.roleId);
        const invalidRoles = valueRoles.filter(role => !rolesAuth.includes(role.trim()));
        if (invalidRoles.length > 0) {
          throw new Error(`Invalid Roles - ${invalidRoles.join(",")}. Accepted roles are: ${rolesAuth.join(", ")}`);
        }
      } else if (column === "customers" && value) {
        const valueCustomers = getTrimmedList(value).map(e => e.toLowerCase());
        const acceptedCustomerNames = choicesCustomer.map(e => e.name.toLowerCase());
        const invalidCustomers = valueCustomers.filter(customer => !acceptedCustomerNames.includes(customer));
        if (invalidCustomers.length > 0) {
          throw new Error(`Invalid customers - ${invalidCustomers.join(",")}. Accepted customers are: ${acceptedCustomerNames.join(", ")}`);
        }
      }
    };

  const onUpload = async values => {
    const { file } = values;

    try {
      let { rows, errors } = await readXlsxFile(file.rawFile, {
        schema: getSchema()
      });
      if (errors && errors.length > 0) {
        const errorList = errors.map(({ error, row, column }) => ({ row, column, message: error }));
        setErrorList(errorList);
        return;
      }
      //validate
      errors = [];
      rows.forEach((row, idx) => {
        if (!row.fullName) {
          errors.push({ row: idx + 1, column: "FULL NAME", message: "Full Name required." });
        }
        if (!row.username) {
          errors.push({ row: idx + 1, column: "USERNAME", message: "Username required." });
        }
        if (!row.mobile) {
          errors.push({ row: idx + 1, column: "MOBILE", message: "Mobile number required." });
        }
        if (!row.roles) {
          errors.push({ row: idx + 1, column: "ROLES", message: "Roles required." });
        } else {
          const valueRoles = getTrimmedList(row.roles)?.map(e => e.toLowerCase());
          if (valueRoles.includes(Role.MERCHANT) && !row.customers) {
            errors.push({ row: idx + 1, column: "CUSTOMERS", message: "Customers required." });
          }
        }
      });
      if (errors.length > 0) {
        setErrorList(errors);
        return;
      }

      // parsing
      rows = rows.map(e => ({
        ...e,
        apps: [
          {
            id: app?.id,
            customerIds: e.customers
              ? getTrimmedList(e.customers).map(
                  customerName => choicesCustomer.find(customer => customer.name.toLowerCase() === customerName.toLowerCase())?.id
                )
              : null
          }
        ]
      }));

      // First Upload in Auth Server
      let options = {
        url: `${authServerUrl}/users/batchSave`,
        method: "post",
        data: rows,
        headers: {
          "x-api-key": apiKey
        }
      };

      dispatch({ type: FETCH_START });
      dispatch({ type: SAVING_START });

      let response = await axios.request(options);
      const iamUsers = response.data;

      // Then Upload User in App
      const appUsers = rows.map(user => {
        const au = iamUsers.find(u => u.username === user.username);
        return {
          ...user,
          id: au && au.id,
          roles: au.roleList && au.roleList.map(r => r.role && r.role.roleId).join(", ")
        };
      });

      options = { url: "users/batchSave", method: "post", data: appUsers };
      response = await dataProvider(RestMethods.CUSTOM, undefined, options);

      dispatch({ type: FETCH_END });
      dispatch({ type: SAVING_END });
      dispatch(showNotification("js.notification.uploaded", { messageArgs: { smart_count: 1, element: "Users" } }));
      history.push("/users");
    } catch (err) {
      console.log(err);
      if (err.status === 400 && err.errors && Array.isArray(err.errors)) {
        setErrorList(err.errors);
      } else if (err?.response?.status === 400 && Array.isArray(err?.response?.data)) {
        setErrorList(err.response.data);
      } else {
        handleError(err?.response?.data || err, dispatch);
      }
      dispatch({ type: FETCH_END });
      dispatch({ type: SAVING_END });
    }
  };

  return {
    roles,
    permissions,
    appUser,
    app,
    iamUser,
    errorList,
    authState,
    choicesCustomer,
    choicesWarehouse,
    unitList: unitChoices,
    allUnitList,
    setErrorList,
    onSave,
    onUpdate,
    onUpload,
    onDelete,
    dispatch
  };
};

export default useCreateEdit;
